import { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';

import type { LocationCage } from '@/components/organisms/LiceReportLocationTable/LiceReportLocationTable';
import type { LiceReportDataSource } from '@/routes/Client/LiceReport/helpers/liceReportDataSource';
import {
    applySamePeriodCageToggles,
    buildLiceReportSelectionMap,
    mergeWeeklyReportCageToggles,
    resolveWeeklySelectedReports,
    toggleCageIncludedInReports,
} from '@/routes/Client/LiceReport/helpers/liceReportSelection';
import type {
    LiceReportClientLocation,
    LiceReportConfiguration,
    LiceReportResult,
    LiceReportWeekNumber,
    LiceReportWithTotals,
} from '@/routes/Client/LiceReport/types';

export type UseLiceReportSelectionParams = {
    reports: LiceReportResult[];
    weeklyReports: LiceReportResult[];
    mode: 'weekly' | 'daily';
    dataSourceType: LiceReportDataSource;
    clientLocations: LiceReportClientLocation[];
    /** When set, only this location is kept in the selection map (location page). */
    locationId?: number;
    onDataSourceChange: (dataSourceType: LiceReportDataSource) => void;
    /** The weeks the selector settled on, including the fallback to the newest week. */
    onWeeklySelectionChange?: (weekNumbers: LiceReportWeekNumber[]) => void;
};

export type UseLiceReportSelectionResult = {
    selectedReports: LiceReportWithTotals[];
    noData: boolean;
    handleCageSelected: (reportIndex: number, locationId: number, cage: LocationCage) => void;
    onConfigurationChangeHandler: (config: LiceReportConfiguration) => void;
};

/**
 * Which reports the table shows.
 *
 * Derived while rendering. The previous version wrote `selectedReports` in an effect, so the
 * frame after the request finished had neither the skeleton nor the table.
 */
export function useLiceReportSelection({
    reports,
    weeklyReports,
    mode,
    dataSourceType,
    clientLocations,
    locationId,
    onDataSourceChange,
    onWeeklySelectionChange,
}: UseLiceReportSelectionParams): UseLiceReportSelectionResult {
    const [reportConfiguration, setReportConfiguration] = useState<LiceReportConfiguration>({
        dataSourceType,
    });
    const [toggledReports, setToggledReports] = useState<LiceReportWithTotals[]>([]);

    const { map: weekStringToReportMap, keysInOrder } = useMemo(
        () =>
            buildLiceReportSelectionMap({
                reports,
                clientLocations,
                locationId,
            }),
        [reports, clientLocations, locationId]
    );

    const noData = keysInOrder.length === 0;

    const selectedReports = useMemo(() => {
        if (mode === 'daily') {
            const firstKey = keysInOrder[0];
            if (firstKey === undefined) {
                return [];
            }
            const firstReport = weekStringToReportMap.get(firstKey);
            return firstReport === undefined
                ? []
                : [applySamePeriodCageToggles(firstReport, toggledReports)];
        }

        const weekNumbers = reportConfiguration.weekNumbers;
        if (weekNumbers !== undefined && weekNumbers.length > 0 && weekStringToReportMap.size > 0) {
            const picked = weekNumbers
                .map(({ key }) => weekStringToReportMap.get(key))
                .filter((report): report is LiceReportWithTotals => report !== undefined);
            return mergeWeeklyReportCageToggles(picked, toggledReports);
        }

        if (weekStringToReportMap.size === 0) {
            return [];
        }

        return mergeWeeklyReportCageToggles(
            resolveWeeklySelectedReports(toggledReports, keysInOrder, weekStringToReportMap),
            toggledReports
        );
    }, [mode, keysInOrder, weekStringToReportMap, reportConfiguration.weekNumbers, toggledReports]);

    const handleCageSelected = useCallback(
        (reportIndex: number, cageLocationId: number, cage: LocationCage) => {
            setToggledReports(
                toggleCageIncludedInReports(
                    selectedReports,
                    reportIndex,
                    cageLocationId,
                    cage.cageId
                )
            );
        },
        [selectedReports]
    );

    const onConfigurationChangeHandler = useCallback(
        (config: LiceReportConfiguration) => {
            const effectiveConfig =
                config.weekNumbers != null && config.weekNumbers.length === 0
                    ? {
                          ...config,
                          weekNumbers: weeklyReports[0]
                              ? [
                                    {
                                        weekNumber: weeklyReports[0].isoWeek,
                                        year: weeklyReports[0].isoYear,
                                        interimReport: weeklyReports[0].interimReport,
                                        key: `${weeklyReports[0].isoWeek}-${weeklyReports[0].isoYear}`,
                                    },
                                ]
                              : [],
                      }
                    : config;

            setReportConfiguration((prevConfig) => {
                const mergedConfig = { ...prevConfig, ...effectiveConfig };
                if (isEqual(prevConfig, mergedConfig)) {
                    return prevConfig;
                }
                return mergedConfig;
            });
            if (effectiveConfig.weekNumbers) {
                onWeeklySelectionChange?.(effectiveConfig.weekNumbers);
            }
            if (effectiveConfig.dataSourceType) {
                onDataSourceChange(effectiveConfig.dataSourceType);
            }
        },
        [weeklyReports, onDataSourceChange, onWeeklySelectionChange]
    );

    return {
        selectedReports,
        noData,
        handleCageSelected,
        onConfigurationChangeHandler,
    };
}
